<template>
  <div
    class="checkboxes"
    :class="{
      'checkboxes-error-state': errorMessages.length,
      'mb-0': noMarginBottom
    }"
  >
    <v-row dense>
      <v-col class="text-left checkbox-label-wrapper">
        <slot name="label"
          ><strong>{{ label }}</strong></slot
        >
      </v-col>
      <v-col
        v-if="multiple && !hideSelectAll"
        class="checkboxes-selections"
        :style="`justify-content: ${selectOptionsAlign};`"
      >
        <button
          type="button"
          :disabled="disabled || selectAllDisabled"
          @click="selectAll"
        >
          {{ $t("common:mobile.filters.selectAll.label") }}
        </button>
        <div class="checkboxes-selections__divider" />
        <button
          type="button"
          :disabled="disabled || !anyItem"
          @click="deselectAll"
        >
          {{ $t("common:mobile.filters.deselectAll.label") }}
        </button>
      </v-col>
    </v-row>
    <v-progress-linear :active="loading" :indeterminate="loading" />

    <div
      class="checkboxes-inputs"
      :class="multiple ? 'checkboxes-multiple-input' : ''"
    >
      <template v-for="(item, index) in items">
        <template v-if="item.divider">
          <div :key="index" class="checkbox-divider"><v-divider /></div
        ></template>
        <template
          v-else-if="item.header || (item.children && item.children.length > 0)"
          ><div :key="index" class="checkbox-header">
            {{ item[itemText] }}
            <div
              v-if="multiple && item.children && item.children.length > 0"
              class="group-all-selections"
            >
              <button
                type="button"
                data-test="selectGroup"
                :disabled="disabled"
                @click="selectGroup(item)"
              >
                {{ $t("common:mobile.filters.selectAll.label") }}
              </button>
              <div class="all-selection-divider">|</div>
              <button
                type="button"
                data-test="deselectGroup"
                :disabled="disabled"
                @click="deselectGroup(item)"
              >
                {{ $t("common:mobile.filters.deselectAll.label") }}
              </button>
            </div>
          </div>
        </template>
        <div
          v-else
          :key="index"
          class="checkbox-input"
          :class="checkboxInputClass(item)"
          :style="checkBoxInputStyle(item)"
          :aria-disabled="disabled"
          :aria-checked="itemSelected(item)"
          @click="clickCheckbox(item)"
        >
          <slot
            name="checkbox-content"
            v-bind="{
              item,
              itemSelected: itemSelected(item)
            }"
          >
            <v-icon
              v-if="
                (multiple || !boxOutlined) &&
                  (anyItem || showGreyUnselectedDefault)
              "
              class="mr-2"
              style="color: currentColor;"
            >
              {{ itemSelected(item) ? "$iconCheck" : multiple ? "$iconMinus" : "" }}
            </v-icon>
            {{ item[itemText] }}
          </slot>
        </div>
      </template>
    </div>

    <div v-if="errorMessages.length">
      <ul class="error-messages">
        <li v-for="(err, i) in errorMessages" :key="i">
          {{ err }}
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import { defineComponent } from "vue";
import { uniqBy } from "lodash-es";

export default defineComponent({
  name: "AppSelectCheckboxes",
  props: {
    value: {
      type: [Array, String, Object, Boolean],
      default: null
    },
    // Enable if at least one value should be kept selected
    valueRequired: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ""
    },
    itemText: {
      type: String,
      default: "text"
    },
    itemValue: {
      type: String,
      default: "value"
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    useItemColor: {
      type: Boolean,
      default: false
    },
    boxColor: {
      type: String,
      default: "primary"
    },
    boxOutlined: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    noMarginBottom: {
      type: Boolean,
      default: false
    },
    selectOptionsAlign: {
      type: String,
      default: "flex-end"
    },
    showGreyUnselectedDefault: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    hideSelectAll: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    emittedValues: {
      get() {
        if (this.multiple) return this.value || [];
        return this.value;
      },
      set(v) {
        if (this.multiple && v == null) {
          this.$emit("input", []);
        } else {
          this.$emit("input", v);
        }
      }
    },
    selectedValues() {
      if (
        this.emittedValues == null ||
        (Array.isArray(this.emittedValues) && this.emittedValues.length == 0)
      ) {
        return [];
      }

      if (this.multiple) {
        if (this.returnObject)
          return this.emittedValues.map(x => x[this.itemValue]);
        return this.emittedValues;
      } else {
        if (this.returnObject)
          return [this.emittedValues[this.itemValue]].flat();
        else return [this.emittedValues].flat();
      }
    },
    anyItem() {
      return (
        this.emittedValues != null &&
        (Array.isArray(this.emittedValues)
          ? this.emittedValues.length > 0
          : true)
      );
    },
    selectAllDisabled() {
      if (Array.isArray(this.emittedValues)) {
        return this.emittedValues.length == this.items.length;
      } else {
        return this.emittedValues;
      }
    }
  },
  created() {
    if (this.multiple && this.value == null) {
      this.emittedValues = [];
    }
  },
  methods: {
    clickCheckbox(item) {
      if (this.disabled) return;
      this.mutateValue(item);
      if (item.emit && typeof item.emit === "function") {
        item.emit(item);
      }
    },
    mutateValue(v) {
      const value = v[this.itemValue];

      if (this.itemSelected(v)) {
        // If value is required, do not allow to remove last option/selection
        if (this.valueRequired && this.selectedValues.length == 1) return;

        if (this.multiple) {
          if (this.returnObject) {
            this.emittedValues = this.emittedValues.filter(
              x => x[this.itemValue] != value
            );
          } else {
            this.emittedValues = this.emittedValues.filter(x => x != value);
          }
        } else {
          this.emittedValues = null;
        }
      } else if (this.multiple) {
        if (this.returnObject) {
          this.emittedValues = [...this.emittedValues, v];
        } else {
          if (Array.isArray(value)) {
            this.emittedValues = [...this.emittedValues, ...value];
          } else {
            this.emittedValues = [...this.emittedValues, value];
          }
        }
      } else {
        if (this.returnObject) this.givenValue = v;
        else this.emittedValues = value;
      }
    },
    selectAll() {
      if (this.disabled) return;
      if (this.returnObject) this.emittedValues = this.items;
      else this.emittedValues = this.items.map(x => x[this.itemValue]);
    },
    deselectAll() {
      if (this.disabled) return;
      this.emittedValues = null;
    },
    itemSelected(item) {
      if (Array.isArray(item[this.itemValue])) {
        return this.selectedValues
          .join(" ")
          .includes(item[this.itemValue].join(" "));
      }
      return this.selectedValues.includes(item[this.itemValue]);
    },
    checkboxInputClass(item) {
      const cssClass = [];

      if (this.disabled) {
        cssClass.push("disabled");
      }

      if (this.useItemColor && item.color) {
        cssClass.push(item.color);
      } else cssClass.push(this.boxColor);

      if (!this.itemSelected(item)) {
        if (this.multiple && (this.anyItem || this.showGreyUnselectedDefault))
          cssClass.push("not-selected");
        if (!this.multiple && this.anyItem) cssClass.push("not-selected");
        if (this.boxOutlined) cssClass.push("checkbox-input__outlined");
        if (this.boxOutlined && !this.multiple && !this.anyItem)
          cssClass.push("outlined-none-selected");
      }
      return cssClass.join(" ");
    },
    checkBoxInputStyle(item) {
      const cssStyle = [];
      if (this.boxOutlined) {
        if (!this.multiple && !this.anyItem) {
          if (this.useItemColor && item.color)
            cssStyle.push(`border-color: var(--v-${item.color}-base)`);
          else cssStyle.push(`color: var(--v-${this.boxColor}-base)`);
          return;
        }
      }
      if (!this.itemSelected(item)) {
        if (this.boxOutlined) {
          if (this.useItemColor && item.color) {
            cssStyle.push(`border-color: var(--v-${item.color}-base)`);
            cssStyle.push(`color: var(--v-${item.color}-base)`);
          } else {
            cssStyle.push(`border-color: var(--v-${this.boxColor}-base)`);
            cssStyle.push(`color: var(--v-${this.boxColor}-base)`);
          }
        }
      }
      return cssStyle.join(";");
    },

    selectGroup(item) {
      let currentValues = this.emittedValues;

      if (this.returnObject) {
        this.emittedValues = uniqBy(
          currentValues.concat(item.children).concat([item]),
          this.itemValue
        );
        return;
      }

      this.emittedValues = uniqBy(
        currentValues.concat(
          item.children
            .map(x => x[this.itemValue])
            .concat([item[this.itemValue]])
        )
      );
    },
    deselectGroup(item) {
      const childrenIds = item.children.map(x => x[this.itemValue]);
      childrenIds.push(item[this.itemValue]);

      if (this.returnObject) {
        this.emittedValues = this.emittedValues.filter(cv =>
          childrenIds.includes(cv[this.itemValue]) ? false : true
        );
        return;
      }

      this.emittedValues = this.emittedValues.filter(cv =>
        childrenIds.includes(cv) ? false : true
      );
    }
  }
});
</script>
<style lang="scss" scoped>
.checkboxes-error-state {
  .checkbox-label-wrapper {
    color: var(--v-error-base) !important;
  }
  .checkbox-input {
    background-color: var(--v-error-base) !important;
    border-color: var(--v-error-base) !important;
  }
}

.checkboxes {
  margin-bottom: 20px;
  &-selections {
    display: flex;

    &__divider {
      margin: auto 6px;
      height: 70%;
      width: 1px;
      background-color: var(--v-primary-base);
    }

    button {
      text-decoration: underline;
      font-size: 14px;
      &:disabled {
        color: var(--v-muted-base);
      }
    }
  }

  &-inputs {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-top: 4px;

    .checkbox-divider {
      width: 100%;
      padding: 12px 0px;
    }

    .checkbox-header {
      display: flex;

      color: rgba(0, 0, 0, 0.6);
      width: 100%;
      font-size: 1rem;
      font-weight: 400;

      .group-all-selections {
        margin-left: 18px;
        font-size: 80%;
        align-self: center;
        display: flex;

        .all-selection-divider {
          margin: auto 6px;
        }

        button {
          text-decoration: underline;
        }
      }
    }

    .checkbox-input {
      height: 30px;
      margin-bottom: 0.45em;
      margin-right: 1em;
      border-radius: 2px;
      font-size: 11px;
      padding: 1em;
      color: white;
      cursor: pointer;
      border: 1px solid;

      &.disabled {
        opacity: 0.55;
        pointer-events: none;
        cursor: default;
      }

      display: flex;
      align-items: center;
      &__outlined {
        &:not(.outlined-none-selected) {
          background-color: transparent !important;
        }
        border-color: currentColor;
      }
      .v-icon {
        font-size: 11px;
        color: white;
      }
      &.not-selected {
        opacity: 0.55;
      }
    }
  }
}

.error-messages {
  list-style: none;
  color: var(--v-error-base) !important;
  border-color: var(--v-error-base) !important;
  padding: 0 12px;
  margin-top: 6px;
  font-size: 12px;
}
</style>
